import { useState, useCallback } from 'react';

/**
 * Custom hook for managing modal state and handlers
 * Handles all modal visibility, state management, and modal-specific actions
 */
export const useModalManagement = ({
  onModalAction
}) => {
  // Modal state
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showInstructionsTooltip, setShowInstructionsTooltip] = useState(false);
  const [showSmartSelectionInfo, setShowSmartSelectionInfo] = useState(false);

  // Modal handlers
  const openSummaryModal = useCallback(() => {
    setShowSummaryModal(true);
  }, []);

  const closeSummaryModal = useCallback(() => {
    setShowSummaryModal(false);
  }, []);

  const openDeleteConfirm = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const closeDeleteConfirm = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  const toggleInstructionsTooltip = useCallback(() => {
    setShowInstructionsTooltip(prev => !prev);
  }, []);

  const closeInstructionsTooltip = useCallback(() => {
    setShowInstructionsTooltip(false);
  }, []);

  const openSmartSelectionInfo = useCallback(() => {
    setShowSmartSelectionInfo(true);
  }, []);

  const closeSmartSelectionInfo = useCallback(() => {
    setShowSmartSelectionInfo(false);
  }, []);

  // Modal action handlers
  const handleDeleteRecording = useCallback(async () => {
    try {
      if (onModalAction?.handleDeleteRecording) {
        await onModalAction.handleDeleteRecording();
      }
      closeDeleteConfirm();
    } catch (error) {
      console.error('Error deleting recording:', error);
    }
  }, [onModalAction, closeDeleteConfirm]);

  const handleStartFresh = useCallback(async () => {
    try {
      if (onModalAction?.handleStartFresh) {
        await onModalAction.handleStartFresh();
      }
    } catch (error) {
      console.error('Error starting fresh:', error);
    }
  }, [onModalAction]);

  const handleResumeRecording = useCallback(async () => {
    try {
      if (onModalAction?.handleResumeRecording) {
        await onModalAction.handleResumeRecording();
      }
    } catch (error) {
      console.error('Error resuming recording:', error);
    }
  }, [onModalAction]);

  const handleUploadRetry = useCallback(() => {
    if (onModalAction?.handleUploadRetry) {
      onModalAction.handleUploadRetry();
    }
  }, [onModalAction]);

  const handleContinueRecording = useCallback(() => {
    if (onModalAction?.handleContinueRecording) {
      onModalAction.handleContinueRecording();
    }
  }, [onModalAction]);

  return {
    // Modal state
    showSummaryModal,
    showDeleteConfirm,
    showInstructionsTooltip,
    showSmartSelectionInfo,
    
    // Modal setters
    setShowSummaryModal,
    setShowDeleteConfirm,
    setShowInstructionsTooltip,
    setShowSmartSelectionInfo,
    
    // Modal handlers
    openSummaryModal,
    closeSummaryModal,
    openDeleteConfirm,
    closeDeleteConfirm,
    toggleInstructionsTooltip,
    closeInstructionsTooltip,
    openSmartSelectionInfo,
    closeSmartSelectionInfo,
    
    // Modal action handlers
    handleDeleteRecording,
    handleStartFresh,
    handleResumeRecording,
    handleUploadRetry,
    handleContinueRecording
  };
};
